import {
  PlusIcon,
  TrashIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { TargetRateInput } from "_graphql/__generated__/globalTypes";
import { CreateTargetRatesVariables } from "_graphql/mutation/__generated__/CreateTargetRates";
import { TextInput } from "components/core";
import CurrencyPicker from "components/core/currency-picker";
import { FormikProps, FormikProvider, getIn } from "formik";
import { FC, useState } from "react";
import clsx from "clsx"; // For conditional classes

export interface CreateTargetRateFormProps {
  form: FormikProps<CreateTargetRatesVariables>;
}

// Removed isErrorObject and getErrorMessage as TextInput/CurrencyPicker should handle errors

const CreateTargetRateForm: FC<CreateTargetRateFormProps> = ({ form }) => {
  const [currentRateIndex, setCurrentRateIndex] = useState(0);

  // Removed renderFieldError - relying on components for error display

  const initialRate: TargetRateInput = {
    rate_value: 0,
    currency_code: "GHS", // Default to GHS or perhaps make it configurable/empty
    currency: "Ghana Cedis",
  };

  const addRate = () => {
    const newRates = [...form.values.rates, { ...initialRate }];
    form.setFieldValue("rates", newRates);
    // Automatically switch to the newly added tab
    setCurrentRateIndex(newRates.length - 1);
  };

  const removeRate = (index: number) => {
    const rates = [...form.values.rates];
    rates.splice(index, 1);
    form.setFieldValue("rates", rates);

    // Adjust current index if necessary
    if (currentRateIndex >= rates.length) {
      setCurrentRateIndex(Math.max(0, rates.length - 1));
    } else if (currentRateIndex > index) {
      // If removing a tab before the current one, shift index back
      setCurrentRateIndex(currentRateIndex - 1);
    }
    // If removing the current tab and it's not the last one, the index remains the same
    // as the next tab shifts into its place. If it *is* the last one, the first check handles it.
  };

  return (
    <FormikProvider value={form}>
      {/* Use Formik's form element for better semantics if needed, though div is fine */}
      <div className="space-y-6 py-4 no-scrollbar">
        {/* Enhanced Alert Box (consistent with UpdateTargetRateForm) */}
        <div
          className="bg-blue-50 border border-blue-300 text-blue-800 px-4 py-3 rounded-lg flex items-start space-x-3"
          role="alert"
        >
          <InformationCircleIcon className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold">Rate Configuration</h3>
            <p className="text-sm">
              Configure rates for the target. Add multiple rates with different
              currencies if needed.
            </p>
          </div>
        </div>

        {/* Rate tabs */}
        <div className="flex space-x-2 overflow-x-auto pb-2 border-b border-gray-200">
          {form.values.rates.map((rate, index: number) => {
            // Check for errors specific to this rate tab
            const rateErrors = getIn(form.errors, `rates[${index}]`);
            const hasError = rateErrors && Object.keys(rateErrors).length > 0;
            const rateTouched = getIn(form.touched, `rates[${index}]`);
            const isTouchedAndError =
              hasError && rateTouched && Object.keys(rateTouched).length > 0;

            return (
              <div
                key={index}
                className={clsx(
                  "px-3 py-2 text-sm font-medium rounded-t-md cursor-pointer whitespace-nowrap flex items-center space-x-2 transition-colors duration-150 ease-in-out",
                  currentRateIndex === index
                    ? "bg-primary-100 text-primary-700 border-b-2 border-primary-500" // Active tab style
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800", // Inactive tab style
                  isTouchedAndError && currentRateIndex !== index
                    ? "border-b-2 border-red-400"
                    : "", // Error indicator on inactive tabs
                  isTouchedAndError && currentRateIndex === index
                    ? "bg-red-50 border-red-500 text-red-700"
                    : "" // Error indicator on active tab
                )}
                onClick={() => setCurrentRateIndex(index)}
                title={
                  isTouchedAndError
                    ? "This rate has errors"
                    : `Edit ${rate.currency_code} rate`
                }
              >
                <span>{`${rate.currency_code || "N/A"} ${
                  rate?.rate_value?.toFixed(2) || "0.00"
                }`}</span>
                {/* Show remove button only if more than one rate exists */}
                {form.values.rates.length > 1 && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent tab selection when clicking remove
                      removeRate(index);
                    }}
                    className="text-gray-400 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 rounded-full p-0.5"
                    aria-label={`Remove ${rate.currency_code} rate`}
                    title={`Remove ${rate.currency_code} rate`}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            );
          })}
          {/* Add Rate Button */}
          <button
            type="button"
            className="flex items-center justify-center px-3 py-2 bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800 rounded-t-md cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-500"
            onClick={addRate}
            title="Add a new rate"
          >
            <PlusIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Current rate form fields */}
        {form.values.rates.map((_, rateIndex: number) => (
          <div
            key={rateIndex}
            // Use hidden attribute for better accessibility than display: none
            hidden={currentRateIndex !== rateIndex}
            role="tabpanel" // ARIA role for tab content
            aria-labelledby={`rate-tab-${rateIndex}`} // Link to tab control if tabs had IDs
          >
            {/* Render content only for the active tab for slight perf improvement if needed,
                but keeping it simple by just hiding is often fine. */}
            {currentRateIndex === rateIndex && (
              <div className="space-y-5 pt-4">
                {/* Use TextInput which handles its own errors */}
                <TextInput
                  label="Rate Value"
                  id={`rates[${rateIndex}].rate_value`}
                  type="number"
                  placeholder="0.00"
                  min={0}
                  step={0.01} // More appropriate step for currency
                  // Pass necessary Formik props for TextInput to manage state and errors
                  {...form}
                  required // Mark as required based on schema
                />

                {/* Use CurrencyPicker */}
                <CurrencyPicker
                  label="Currency"
                  id={`rates[${rateIndex}].currency_code`}
                  // Pass necessary Formik props
                  {...form}
                  placeholder="Select currency"
                  required // Mark as required based on schema
                />
                {/* Hidden input for currency name - managed by CurrencyPicker's setFieldValue */}
                {/* <input type="hidden" name={`rates[${rateIndex}].currency`} value={getIn(form.values, `rates[${rateIndex}].currency`) || ''} /> */}
                {/* Note: No need for the hidden input above if CurrencyPicker correctly sets the value via form.setFieldValue */}
              </div>
            )}
          </div>
        ))}
      </div>
    </FormikProvider>
  );
};

export default CreateTargetRateForm;
